import { z } from "zod"

import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const idempotencyKey = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/)
const reasonCode = z.string().regex(/^[a-z][a-z0-9_]{1,63}$/)

export const inventoryConsumptionSynchronizeV1Schema = z
  .object({
    requestVersion: z.literal(1),
    correlationId: correlationIdSchema,
    idempotencyKey,
    reconciliationId: entityIdSchema,
    expectedVersion: z.literal(0),
    provisionalMaterialUseId: entityIdSchema,
    movementId: entityIdSchema,
    exceptionId: entityIdSchema,
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    itemMasterId: entityIdSchema,
    postingDate: z.string().date(),
    billingDisposition: z.enum(["billable", "included", "non_billable"]),
    billingReferenceId: entityIdSchema.nullable(),
    reasonCode,
  })
  .strict()
  .superRefine((value, context) => {
    if (
      (value.billingDisposition === "billable") !==
      (value.billingReferenceId !== null)
    ) {
      context.addIssue({
        code: "custom",
        message: "Billable consumption requires one billing reference.",
      })
    }
  })

export const inventoryConsumptionResultV1Schema = z
  .object({
    entityType: z.literal("inventory_consumption_reconciliation"),
    entityId: entityIdSchema,
    state: z.enum(["Posted", "Transfer Suggested", "Inventory Exception"]),
    version: z.literal(1),
    provisionalMaterialUseId: entityIdSchema,
    visitId: entityIdSchema,
    workerId: entityIdSchema,
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    targetLocationId: entityIdSchema,
    itemMasterId: entityIdSchema,
    quantityMilli: z.number().int().positive(),
    targetActualBeforeMilli: z.number().int().nonnegative(),
    targetAvailableBeforeMilli: z.number().int().nonnegative(),
    movementId: entityIdSchema.nullable(),
    transferSourceLocationId: entityIdSchema.nullable(),
    exceptionId: entityIdSchema.nullable(),
    stockPosted: z.boolean(),
    negativeStockPrevented: z.literal(true),
    valuationAmountMinor: z.number().int().nonnegative().nullable(),
    visitCostImpactMinor: z.number().int().nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    const posted = value.state === "Posted"
    const transfer = value.state === "Transfer Suggested"
    const exception = value.state === "Inventory Exception"
    if (
      posted !== value.stockPosted ||
      posted !== (value.movementId !== null) ||
      posted !== (value.valuationAmountMinor !== null) ||
      posted !== (value.visitCostImpactMinor !== null) ||
      transfer !== (value.transferSourceLocationId !== null) ||
      exception !== (value.exceptionId !== null)
    ) {
      context.addIssue({
        code: "custom",
        message: "Consumption outcome references do not match its state.",
      })
    }
  })

export type InventoryConsumptionSynchronizeV1 = z.infer<
  typeof inventoryConsumptionSynchronizeV1Schema
>
export type InventoryConsumptionResultV1 = z.infer<
  typeof inventoryConsumptionResultV1Schema
>
